import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { triggerCartShuffleTimeoutErrorPath } from '@hungryroot/component-error-boundary';
import { type OrderDetail } from '@hungryroot/rest/api/v2/customers/[id]/orders/[order_id]/get';
import { useGetOrder } from './useGetOrder';
import { useGetOrderShufflingStatus } from './useGetOrderShufflingStatus';

interface UsePollGetOrderNotShufflingProps {
  params: {
    orderId?: number;
    inventoryDate?: string;
  };
  options?: {
    enabled?: boolean;
  };
}

interface OrderDetailQueryDataWithMeta extends OrderDetail {
  _meta?: {
    invalidated?: boolean;
  };
}

export function usePollGetOrderNotShuffling(
  props: UsePollGetOrderNotShufflingProps
) {
  const { params, options = {} } = props;
  const { orderId } = params;
  const { enabled = true } = options;

  const queryClient = useQueryClient();

  const {
    data: order,
    isLoading: isLoadingOrder,
    isFetching: isFetchingOrder,
  } = useGetOrder(orderId, {
    enabled: enabled && Number.isFinite(orderId),
  });

  const {
    data: polledOrderShufflingStatus,
    isLoading: isLoadingPolledOrderShufflingStatus,
    isFetching: isFetchingPolledOrderShufflingStatus,
  } = useGetOrderShufflingStatus({
    orderId,
    options: {
      ...options,
      refetchInterval(query) {
        const { data, dataUpdateCount } = query.state;

        if (data?.is_shuffling === false) {
          return false;
        }

        if (dataUpdateCount >= 50) {
          triggerCartShuffleTimeoutErrorPath();
        } else if (dataUpdateCount >= 10) {
          return 3000;
        } else if (dataUpdateCount >= 5) {
          return 1500;
        } else if (dataUpdateCount >= 1) {
          return 1000;
        } else {
          return 3000;
        }
      },
      enabled:
        enabled && Number.isFinite(orderId) && order?.is_shuffling === true,
    },
  });

  // Checks if the the order entity is still in a shuffling state despite the order
  // shuffling status having returned false. If so, invalidate the order in the cache
  // to trigger a re-fetch.
  useEffect(() => {
    if (
      polledOrderShufflingStatus?.is_shuffling === false &&
      order?.is_shuffling === true
    ) {
      const queryKey = ['Orders', 'bundle order', orderId];

      const oldData =
        queryClient.getQueryData<OrderDetailQueryDataWithMeta>(queryKey);

      if (oldData?._meta?.invalidated) {
        return;
      }

      queryClient.setQueryData<OrderDetailQueryDataWithMeta>(
        queryKey,
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            // Sets metadata on the order data so we know when it's been invalidated.
            // This prevents the case where there are multiple consumers of the
            // `usePollGetOrderNotShuffling()` hook causing multiple re-invalidations at
            // the same time.
            _meta: {
              invalidated: true,
            },
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey,
        exact: true,
      });
    }
  }, [
    order?.is_shuffling,
    orderId,
    polledOrderShufflingStatus?.is_shuffling,
    queryClient,
  ]);

  return {
    data: order,
    isLoading: isLoadingOrder || isLoadingPolledOrderShufflingStatus,
    isFetching: isFetchingOrder || isFetchingPolledOrderShufflingStatus,
  };
}
